import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown, MapPin, Clock } from "lucide-react";
import { SilhouetteVehicle } from "./SilhouetteVehicle";

/**
 * DeliveryRow — ligne signature d'une livraison dans le planning.
 * Compacte, orientée « lecture cerveau » : heure → véhicule → commercial → manque.
 *
 * L'heure est l'élément principal.
 * La miniature véhicule ne prend que ~15 % de la ligne.
 * Le reste est de l'information de pilotage.
 *
 * Variantes visuelles :
 *   • state="imminente"  → row élargie, halo lime subtil, countdown
 *   • state="encours"    → chip noir + point lime pulsant
 *   • state="avenir"     → row standard, monochrome
 *   • state="terminee"   → row réduite, grisée (visible dans la section repliée)
 */
export function DeliveryRow({
  delivery,
  index = 0,
  highlight = false, // pour la carte "Prochaine" du bloc Next Up
  testid,
}) {
  const {
    time,
    vehicle,
    client,
    commercial,
    location,
    silhouette = "berline",
    image = null,
    state = "avenir",
    countdown = null,
    checklist = [],
  } = delivery;

  const isImminent = state === "imminente";
  const isEncours = state === "encours";
  const isDone = state === "terminee";
  const enlarged = isImminent || highlight;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: 0.03 * index,
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ scale: enlarged ? 1 : 1.005 }}
      data-testid={testid}
      className={`group relative overflow-hidden rounded-[18px] transition-all duration-300 ${
        enlarged
          ? "bg-white shadow-[0_1px_2px_rgba(21,21,21,0.05),0_20px_48px_-20px_rgba(200,255,47,0.35),inset_0_0_0_1px_rgba(200,255,47,0.3)]"
          : "bg-white shadow-wyvio-elev hover:shadow-wyvio-elev-hover"
      }`}
    >
      <div
        className={`flex items-center gap-6 ${
          enlarged ? "px-6 py-6" : "px-6 py-4"
        }`}
      >
        {/* Heure — élément principal */}
        <div className="w-[110px] flex-shrink-0">
          <span
            className={`wyvio-tabular block font-medium leading-none tracking-[-0.035em] ${
              isDone ? "text-[#B8B8B0]" : "text-[#151515]"
            }`}
            style={{ fontSize: enlarged ? "44px" : "34px" }}
          >
            {time}
          </span>
          <p
            className={`mt-2 text-[10.5px] font-medium uppercase tracking-[0.22em] ${
              isDone ? "text-[#C4C4B8]" : "text-[#8A8A80]"
            }`}
          >
            {commercial}
          </p>
        </div>

        {/* Séparateur fin */}
        <div
          aria-hidden
          className="h-14 w-px flex-shrink-0"
          style={{ background: "#EEEEE8" }}
        />

        {/* Miniature véhicule — 20 % max */}
        <div
          className={`flex-shrink-0 overflow-hidden rounded-[10px] ${
            enlarged ? "h-[74px] w-[120px]" : "h-[54px] w-[92px]"
          }`}
        >
          {image ? (
            <img
              src={image}
              alt={vehicle}
              className="h-full w-full object-cover"
            />
          ) : (
            <SilhouetteVehicle
              type={silhouette}
              variant={isDone ? "light" : "dark"}
              className="h-full w-full"
            />
          )}
        </div>

        {/* Infos véhicule + client */}
        <div className="min-w-0 flex-1">
          <p
            className={`truncate font-medium tracking-[-0.015em] ${
              enlarged ? "text-[19px]" : "text-[16px]"
            } ${isDone ? "text-[#8A8A80]" : "text-[#151515]"}`}
          >
            {vehicle}
          </p>
          <div className="mt-1 flex items-center gap-3 text-[12.5px]">
            <span className={isDone ? "text-[#B8B8B0]" : "text-[#6B6B67]"}>
              {client}
            </span>
            {location && !isDone && (
              <span className="hidden items-center gap-1 text-[#8A8A80] xl:inline-flex">
                <MapPin className="h-3 w-3" strokeWidth={1.8} />
                {location}
              </span>
            )}
          </div>
        </div>

        {/* Checklist condensée */}
        {!isDone && checklist.length > 0 && (
          <div className="hidden flex-shrink-0 items-center gap-3 md:flex">
            {checklist.map((c, i) => (
              <div key={i} className="flex items-center gap-1">
                {c.done ? (
                  <Check
                    className="h-3.5 w-3.5 text-[#151515]"
                    strokeWidth={2.4}
                  />
                ) : (
                  <Clock
                    className="h-3.5 w-3.5 text-[#B7292E]"
                    strokeWidth={2}
                  />
                )}
                <span
                  className={`text-[11.5px] font-medium tracking-[-0.005em] ${
                    c.done ? "text-[#151515]" : "text-[#B7292E]"
                  }`}
                >
                  {c.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Chip statut à droite */}
        <div className="flex-shrink-0">
          {isImminent && countdown && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C8FF2F] px-3 py-1.5 text-[10.5px] font-medium uppercase tracking-[0.18em] text-[#151515]">
              <span
                aria-hidden
                className="wyvio-segment-active h-1.5 w-1.5 rounded-full"
                style={{ background: "#151515" }}
              />
              {countdown}
            </span>
          )}
          {isEncours && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#151515] px-3 py-1.5 text-[10.5px] font-medium uppercase tracking-[0.18em] text-white">
              <span
                aria-hidden
                className="wyvio-segment-active h-1.5 w-1.5 rounded-full"
                style={{ background: "#C8FF2F" }}
              />
              En cours
            </span>
          )}
          {state === "avenir" && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F2F2EE] px-3 py-1.5 text-[10.5px] font-medium uppercase tracking-[0.18em] text-[#6B6B67]">
              À venir
            </span>
          )}
          {isDone && (
            <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-[#B8B8B0]">
              <Check className="h-3 w-3" strokeWidth={2.4} />
              Livrée
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/**
 * DeliverySection — regroupe des DeliveryRow sous un titre discret.
 * La section "Terminées" est repliable et grisée par défaut.
 */
export function DeliverySection({
  title,
  count,
  children,
  collapsible = false,
  defaultCollapsed = false,
  testid,
}) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const Wrapper = collapsible ? "button" : "div";

  return (
    <section data-testid={testid} className="mb-10">
      <Wrapper
        {...(collapsible ? { onClick: () => setOpen((v) => !v), type: "button" } : {})}
        className="mb-4 flex w-full items-center gap-3 text-left"
      >
        <p className="text-[10.5px] font-medium uppercase tracking-[0.28em] text-[#8A8A80]">
          {title}
        </p>
        <span
          aria-hidden
          className="h-px flex-1"
          style={{ background: "#E8E8E2" }}
        />
        <span className="wyvio-tabular text-[10.5px] font-medium text-[#8A8A80]">
          {String(count).padStart(2, "0")}
        </span>
        {collapsible && (
          <ChevronDown
            className={`h-3.5 w-3.5 text-[#8A8A80] transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
            strokeWidth={2}
          />
        )}
      </Wrapper>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-2.5"
            style={{ overflow: "hidden" }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
